import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import Navbar from './components/navbar/Navbar';

// Configure QueryClient with default options
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
      cacheTime: 5 * 60 * 1000,
      staleTime: 30 * 1000
    },
    mutations: {
      retry: false
    }
  }
});

// Lazy load pages
const HomePage = lazy(() => import('./pages/home/components/HomePage'));

// const SubmissionForm = lazy(() => import('./pages/submissions/components/SubmissionForm'));
const SubmissionForm = lazy(() => import('./pages/submission/SubmissionForm'));
const SubmissionFlowV1 = lazy(() => import('./pages/submission/v1/SubmissionFlowV1'));

const SubmissionList = lazy(() => import('./pages/voting/components/SubmissionVoting'));
const PricingPage = lazy(() => import('./pages/profiles/components/PricingPage'));
const ProfilePage = lazy(() => import('./pages/profiles/components/ProfilePage'));
const PlaylistPage = lazy(() => import('./pages/playlists/components/PlaylistPage'));
const ShopPage = lazy(() => import('./pages/shop/components/ShopPage'));
const PurchaseSuccess = lazy(() => import('./pages/shop/components/PurchaseSuccess'));
const AutomationPage = lazy(() => import('./pages/automation/components/AutomationPage'));
const BrandPage = lazy(() => import('./pages/brand/components/BrandPage'));
const AdminDashboard = lazy(() => import('./pages/admin/components/AdminDashboard'));
const RadarPlayer = lazy(() => import('./pages/radar/RadarPlayer'));
const TodayDashboard = lazy(() => import('./pages/radar/TodayDashboard'));
const NotFound = lazy(() => import('./pages/NotFound'));

const ProfileWrapper = lazy(() => import('./pages/profiles/components/ProfileWrapper'));

// The v1 submission flow is a focused funnel and renders its own wordmark header.
function SiteNavbar() {
  const { pathname } = useLocation();
  if (pathname.startsWith('/submissions/v1')) return null;
  return <Navbar />;
}

function App() {
  const excludeBackendRoutes = ['/admin', '/auth', '/oauth', '/api'];
  const isBackendRoute = excludeBackendRoutes.some((route) =>
    window.location.pathname.startsWith(route)
  );

  if (isBackendRoute) {
    return null;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <div className="app" style={{ backgroundColor: '#FAFAFA' }}>
          <SiteNavbar />
          <main>
            <Suspense fallback={<div>Loading...</div>}>
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/submissions" element={<SubmissionForm />} />
                <Route path="/submissions/v1" element={<SubmissionFlowV1 />} />
                <Route path="/vote" element={<SubmissionList />} />
                <Route path="/membership" element={<PricingPage />} />
                <Route path="/profile" element={<ProfileWrapper />} />
                <Route path="/playlists" element={<PlaylistPage />} />
                <Route path="/shop" element={<ShopPage />} />
                <Route path="/shop/success" element={<PurchaseSuccess />} />
                <Route path="/automation" element={<AutomationPage />} />
                <Route path="/brand" element={<BrandPage />} />
                <Route path="/curator" element={<AdminDashboard />} />
                <Route path="/radar" element={<RadarPlayer />} />
                <Route path="/today" element={<TodayDashboard />} />
                <Route path="/inbox" element={<RadarPlayer />} />
                <Route path="/backlog" element={<RadarPlayer />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </main>
        </div>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
